// La page Messages de contact : boîte de réception des messages envoyés depuis les formulaires du
// site public. Liste paginée filtrée par statut, SECTION d'origine et recherche côté serveur,
// compteurs d'onglets servis par l'API (plus aucun balayage client), détail en modale,
// archivage/réouverture gardés par la permission d'écriture, et rafraîchissement temps réel sur
// l'événement `notification` du sujet « contact-messages ».
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FiArchive, FiCheck, FiCopy, FiInbox, FiMail, FiDownload, FiRefreshCw, FiRotateCcw, FiSearch, FiX } from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { useRealtimeEvent } from "../../Realtime/RealtimeProvider";
import { Modal } from "../../Components/Modal";
import GatedButton from "../../Components/GatedButton";
import { formatExactDateFr, formatRelativeTimeFr } from "../../Helper/dates";
import {
	contactMessagesClient,
	SECTION_NONE,
	type ContactMessage,
	type ContactMessageStatus,
	type ContactSectionCount
} from "./contactMessagesClient";

export const CONTACT_MESSAGES_PAGE_SIZE = 25;
const WRITE_TOKEN = "feature:contact-messages:write";
// L'export est une LECTURE : il ne change aucun statut, la permission de lecture suffit.
const READ_TOKEN = "feature:contact-messages:read";
/** Délai de la recherche : on n'interroge le serveur qu'une fois la frappe posée. */
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_TABS: Array<{ key: ContactMessageStatus | ""; label: string }> = [
	{ key: "new", label: "Nouveaux" },
	{ key: "read", label: "Lus" },
	{ key: "archived", label: "Archivés" },
	{ key: "", label: "Tous" }
];

const EMPTY_LABELS: Record<string, string> = {
	new: "Aucun nouveau message : tout est traité.",
	read: "Aucun message lu pour l'instant.",
	archived: "Aucun message archivé.",
	"": "Aucun message de contact reçu pour l'instant."
};

/** Palette stable des pastilles de section : la même section garde la même teinte. */
const SECTION_PALETTE = [
	"border-sky-200 bg-sky-50 text-sky-700",
	"border-violet-200 bg-violet-50 text-violet-700",
	"border-rose-200 bg-rose-50 text-rose-700",
	"border-teal-200 bg-teal-50 text-teal-700",
	"border-indigo-200 bg-indigo-50 text-indigo-700",
	"border-orange-200 bg-orange-50 text-orange-700",
	"border-cyan-200 bg-cyan-50 text-cyan-700",
	"border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700"
];

const sectionTone = (section: string): string => {
	let h = 0;
	for (let i = 0; i < section.length; i++) h = (h * 31 + section.charCodeAt(i)) >>> 0;
	return SECTION_PALETTE[h % SECTION_PALETTE.length];
};

export const sectionLabelFr = (section: string): string =>
	section === SECTION_NONE ? "Sans section" : section;

/** Pastille de la section d'origine du message (libellé de page ou chemin). */
const SectionPill: React.FC<{ section: string }> = ({ section }) => (
	<span
		data-testid="section-pill"
		className={`inline-flex max-w-[14rem] items-center truncate rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
			section === SECTION_NONE ? "border-slate-200 bg-slate-50 text-slate-500" : sectionTone(section)
		}`}
		title={sectionLabelFr(section)}
	>
		{sectionLabelFr(section)}
	</span>
);

/** Pastille d'environnement d'origine : preview = ambre, production = émeraude. */
const EnvPill: React.FC<{ env: ContactMessage["sourceEnv"] }> = ({ env }) =>
	env === "production" ? (
		<span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
			Production
		</span>
	) : (
		<span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
			Preview
		</span>
	);

/** Squelette d'une ligne pendant le chargement. */
const RowSkeleton: React.FC = () => (
	<div className="animate-pulse space-y-2 px-5 py-4">
		<div className="flex items-center gap-3">
			<div className="h-4 w-40 rounded bg-slate-100" />
			<div className="h-4 w-24 rounded-full bg-slate-100" />
			<div className="ml-auto h-3 w-20 rounded bg-slate-100" />
		</div>
		<div className="h-3 w-2/3 rounded bg-slate-100" />
	</div>
);

interface CountsState {
	new: number;
	read: number;
	archived: number;
}

const ContactMessages: React.FC = () => {
	usePageHeader({ title: "Messages de contact" });

	const [exporting, setExporting] = useState(false);
	const [status, setStatus] = useState<ContactMessageStatus | "">("new");
	const [page, setPage] = useState(0);
	const [items, setItems] = useState<ContactMessage[]>([]);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(true);
	const [counts, setCounts] = useState<CountsState | null>(null);
	const [selected, setSelected] = useState<ContactMessage | null>(null);
	const [emailCopied, setEmailCopied] = useState(false);
	// Filtre par section : "" = toutes ; SECTION_NONE = messages sans section.
	const [section, setSection] = useState("");
	const [sections, setSections] = useState<ContactSectionCount[]>([]);
	// Recherche : searchInput suit la frappe, search (débouncé) déclenche l'appel serveur.
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const loadList = useCallback(async () => {
		setLoading(true);
		try {
			const res = await contactMessagesClient.list({
				status: status || undefined,
				skip: page * CONTACT_MESSAGES_PAGE_SIZE,
				take: CONTACT_MESSAGES_PAGE_SIZE,
				section: section || undefined,
				search: search || undefined
			});
			setItems(res.items);
			setHasMore(res.items.length === CONTACT_MESSAGES_PAGE_SIZE);
			// Les trois compteurs viennent du serveur avec chaque page : plus aucun balayage client.
			if (typeof res.countNew === "number" && typeof res.countRead === "number" && typeof res.countArchived === "number") {
				setCounts({ new: res.countNew, read: res.countRead, archived: res.countArchived });
			} else if (typeof res.countNew === "number") {
				setCounts((current) => (current ? { ...current, new: res.countNew as number } : current));
			}
		} catch {
			setItems([]);
			setHasMore(false);
			toast.error("Échec du chargement des messages de contact.");
		} finally {
			setLoading(false);
		}
	}, [status, page, section, search]);

	/** Les sections distinctes et leur volume : la rangée de filtres sous les onglets. */
	const loadSections = useCallback(async () => {
		try {
			setSections(await contactMessagesClient.sections());
		} catch {
			/* transitoire ; on garde la dernière rangée connue */
		}
	}, []);

	useEffect(() => {
		loadList();
	}, [loadList]);

	useEffect(() => {
		loadSections();
	}, [loadSections]);

	// Temps réel : un nouveau message (sujet « contact-messages ») rafraîchit liste et sections.
	useRealtimeEvent("notification", (payload: any) => {
		if (payload?.topic !== "contact-messages") return;
		loadList();
		loadSections();
	});

	// L'export reprend les filtres affiches : on exporte ce qu'on voit, typiquement les seuls
	// messages « nouveaux ». Aucun statut n'est modifie, ils restent a passer en lu un par un.
	const handleExport = useCallback(async () => {
		if (exporting) return;
		setExporting(true);
		try {
			await contactMessagesClient.exportXlsx({ status: status || undefined, section: section || undefined, search });
			toast.success("Export Excel telecharge.");
		} catch {
			toast.error("Echec de l'export des messages de contact.");
		} finally {
			setExporting(false);
		}
	}, [exporting, status, section, search]);

	const refresh = () => {
		loadList();
		loadSections();
	};

	const changeSearch = (value: string) => {
		setSearchInput(value);
		if (searchTimer.current) clearTimeout(searchTimer.current);
		searchTimer.current = setTimeout(() => {
			setSearch(value.trim());
			setPage(0);
		}, SEARCH_DEBOUNCE_MS);
	};

	const clearSearch = () => {
		if (searchTimer.current) clearTimeout(searchTimer.current);
		setSearchInput("");
		setSearch("");
		setPage(0);
	};

	const changeSection = (key: string) => {
		setSection((current) => (current === key ? "" : key));
		setPage(0);
	};

	/** Change le statut côté serveur puis met à jour la liste/le détail localement (optimiste). */
	const applyStatus = useCallback(
		async (message: ContactMessage, next: ContactMessageStatus, silent = false) => {
			try {
				await contactMessagesClient.setStatus(message.id, next);
				setItems((current) => current.map((m) => (m.id === message.id ? { ...m, status: next } : m)));
				setSelected((current) => (current && current.id === message.id ? { ...current, status: next } : current));
				// Les compteurs d'onglets viennent avec la prochaine liste ; on ajuste localement en attendant.
				setCounts((current) => {
					if (!current) return current;
					const adjusted = { ...current };
					if (message.status !== next) {
						adjusted[message.status] = Math.max(0, adjusted[message.status] - 1);
						adjusted[next] = adjusted[next] + 1;
					}
					return adjusted;
				});
				if (!silent) {
					toast.success(
						next === "archived" ? "Message archivé." : next === "new" ? "Message rouvert." : "Message marqué comme lu."
					);
				}
			} catch {
				if (!silent) toast.error("Échec du changement de statut.");
			}
		},
		[]
	);

	/** Ouvre le détail. Ouvrir ne change JAMAIS le statut : un message ne passe en « lu »
	 *  que par le bouton « Marquer lu » : décision explicite de l'équipe, pour que la file
	 *  « Nouveaux » reste la liste de ce qui n'a pas encore été TRAITÉ, pas juste entrouvert. */
	const openMessage = (message: ContactMessage) => {
		setEmailCopied(false);
		setSelected(message);
	};

	const closeModal = () => {
		setSelected(null);
		// La liste de l'onglet « Nouveaux » peut avoir perdu ce message (marqué lu) : on la resynchronise.
		if (status === "new") loadList();
	};

	const copyEmail = async (email: string) => {
		try {
			await navigator.clipboard.writeText(email);
			setEmailCopied(true);
			toast.success("Adresse e-mail copiée.");
		} catch {
			toast.error("Impossible de copier l'adresse.");
		}
	};

	const changeTab = (key: ContactMessageStatus | "") => {
		setStatus(key);
		setPage(0);
	};

	return (
		<div className="mx-auto max-w-5xl space-y-4 p-6">
			{/* ---- En-tête : titre, compteurs, rafraîchir, onglets de statut ---- */}
			<header className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
				<div className="flex flex-wrap items-center gap-3">
					<div className="min-w-0">
						<p className="text-xs uppercase tracking-widest text-slate-500">Engagement</p>
						<h1 className="text-2xl font-semibold text-slate-900">Messages de contact</h1>
						<p className="text-sm text-slate-500">
							Les messages envoyés depuis le formulaire de contact des sites publics, à traiter puis archiver.
						</p>
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-2">
						{counts && (
							<>
								<span className="inline-flex items-center gap-1.5 rounded-full border border-[#005CA9]/30 bg-[#005CA9]/10 px-3 py-1.5 text-sm font-semibold text-[#005CA9]">
									<span className="h-2 w-2 rounded-full bg-[#005CA9]" aria-hidden />
									{counts.new.toLocaleString("fr-FR")} nouveau{counts.new > 1 ? "x" : ""}
								</span>
								<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-500">
									<FiCheck className="h-3.5 w-3.5" />
									{counts.read.toLocaleString("fr-FR")} lu{counts.read > 1 ? "s" : ""}
								</span>
								<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-500">
									<FiArchive className="h-3.5 w-3.5" />
									{counts.archived.toLocaleString("fr-FR")} archivé{counts.archived > 1 ? "s" : ""}
								</span>
							</>
						)}
						<GatedButton
							token={READ_TOKEN}
							onClick={handleExport}
							title="Exporter les messages filtrés au format Excel"
							className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
						>
							<FiDownload className={`h-4 w-4 ${exporting ? "animate-pulse" : ""}`} />
							{exporting ? "Export en cours…" : "Exporter"}
						</GatedButton>
						<button
							type="button"
							onClick={refresh}
							title="Rafraîchir"
							className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
						>
							<FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
						</button>
					</div>
				</div>

				<div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
					<div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 shadow-sm">
						{STATUS_TABS.map((tab) => (
							<button
								key={tab.key || "all"}
								type="button"
								onClick={() => changeTab(tab.key)}
								className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
									status === tab.key ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
								}`}
							>
								{tab.label}
							</button>
						))}
					</div>
					<label className="relative ml-auto min-w-[14rem] flex-1 sm:max-w-xs">
						<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
						<input
							type="search"
							value={searchInput}
							onChange={(e) => changeSearch(e.target.value)}
							placeholder="Nom, e-mail, sujet ou message"
							aria-label="Rechercher dans les messages"
							className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-8 text-sm text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:ring-2 focus:ring-slate-200"
						/>
						{searchInput && (
							<button
								type="button"
								onClick={clearSearch}
								title="Effacer la recherche"
								className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
							>
								<FiX className="h-3.5 w-3.5" />
							</button>
						)}
					</label>
				</div>

				{/* ---- Filtre par section d'origine (pastilles avec volume, clic = bascule) ---- */}
				{sections.length > 0 && (
					<div className="mt-3 flex flex-wrap items-center gap-1.5">
						<span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Sections</span>
						<button
							type="button"
							onClick={() => changeSection("")}
							className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition ${
								section === ""
									? "border-slate-900 bg-slate-900 text-white"
									: "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
							}`}
						>
							Toutes
						</button>
						{sections.map((s) => (
							<button
								key={s.section}
								type="button"
								onClick={() => changeSection(s.section)}
								title={sectionLabelFr(s.section)}
								className={`inline-flex max-w-[16rem] items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition ${
									section === s.section
										? "border-slate-900 bg-slate-900 text-white"
										: `${s.section === SECTION_NONE ? "border-slate-200 bg-slate-50 text-slate-500" : sectionTone(s.section)} hover:opacity-80`
								}`}
							>
								<span className="truncate">{sectionLabelFr(s.section)}</span>
								<span className={`shrink-0 rounded-full px-1.5 text-[10px] ${section === s.section ? "bg-white/20" : "bg-white/60"}`}>
									{s.count.toLocaleString("fr-FR")}
								</span>
							</button>
						))}
					</div>
				)}
			</header>

			{/* ---- Liste ---- */}
			<div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
				{loading ? (
					<div className="divide-y divide-slate-100">
						<RowSkeleton />
						<RowSkeleton />
						<RowSkeleton />
						<RowSkeleton />
					</div>
				) : items.length === 0 ? (
					<div className="px-6 py-14 text-center">
						<span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
							<FiInbox className="h-6 w-6" />
						</span>
						<p className="mt-3 text-sm font-semibold text-slate-600">{EMPTY_LABELS[status]}</p>
						<p className="mt-1 text-xs text-slate-400">
							{page > 0
								? "Cette page est vide : revenez à la page précédente."
								: "Les nouveaux messages du formulaire public apparaîtront ici."}
						</p>
					</div>
				) : (
					<ul className="divide-y divide-slate-100">
						{items.map((m) => {
							const isNew = m.status === "new";
							return (
								<li key={m.id}>
									<button
										type="button"
										onClick={() => openMessage(m)}
										className="grid w-full grid-cols-1 gap-1 px-5 py-4 text-left transition hover:bg-slate-50 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-3"
									>
										<span className="flex min-w-0 items-center gap-2.5">
											{isNew && <span className="h-2 w-2 shrink-0 rounded-full bg-[#005CA9]" aria-label="Non lu" />}
											<span className={`truncate text-sm ${isNew ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>
												{m.name || "Sans nom"}
											</span>
											<a
												href={`mailto:${m.email}`}
												onClick={(e) => e.stopPropagation()}
												className="hidden truncate text-xs text-[#005CA9] hover:underline sm:inline"
											>
												{m.email}
											</a>
											{m.section && <SectionPill section={m.section} />}
											<EnvPill env={m.sourceEnv} />
										</span>
										<span className="text-xs text-slate-400 sm:text-right" title={formatExactDateFr(m.submittedAt)}>
											{formatRelativeTimeFr(m.submittedAt)}
										</span>
										<span
											className={`truncate text-sm sm:col-span-2 ${isNew ? "font-semibold text-slate-800" : "text-slate-500"}`}
										>
											{m.subject || m.message}
										</span>
									</button>
								</li>
							);
						})}
					</ul>
				)}

				{/* ---- Pagination ---- */}
				<div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/60 px-5 py-3">
					<span className="text-xs text-slate-400">
						Page {(page + 1).toLocaleString("fr-FR")}
						{!loading && ` : ${items.length.toLocaleString("fr-FR")} message${items.length > 1 ? "s" : ""} affiché${items.length > 1 ? "s" : ""}`}
					</span>
					<div className="flex items-center gap-2">
						<button
							type="button"
							disabled={page === 0 || loading}
							onClick={() => setPage((p) => Math.max(0, p - 1))}
							className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-600 shadow-sm transition enabled:hover:border-slate-300 enabled:hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
						>
							Précédent
						</button>
						<button
							type="button"
							disabled={!hasMore || loading}
							onClick={() => setPage((p) => p + 1)}
							className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-600 shadow-sm transition enabled:hover:border-slate-300 enabled:hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
						>
							Suivant
						</button>
					</div>
				</div>
			</div>

			{/* ---- Détail ---- */}
			<Modal show={selected !== null} onHide={closeModal} size="lg" centered>
				{selected && (
					<>
						<Modal.Header closeButton>
							<Modal.Title>{selected.subject || `Message de ${selected.name || selected.email}`}</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<div className="space-y-4">
								{/* Métadonnées : expéditeur, environnement, date exacte, lecture. */}
								<div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:grid-cols-2">
									<div className="min-w-0">
										<p className="text-xs uppercase tracking-wide text-slate-400">Expéditeur</p>
										<p className="truncate text-sm font-semibold text-slate-800">{selected.name || "Sans nom"}</p>
										<span className="mt-0.5 flex items-center gap-1.5">
											<FiMail className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
											<a href={`mailto:${selected.email}`} className="truncate text-sm text-[#005CA9] hover:underline">
												{selected.email}
											</a>
											<button
												type="button"
												onClick={() => copyEmail(selected.email)}
												title="Copier l'adresse e-mail"
												className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-200 hover:text-slate-600"
											>
												{emailCopied ? <FiCheck className="h-3.5 w-3.5 text-emerald-600" /> : <FiCopy className="h-3.5 w-3.5" />}
											</button>
										</span>
									</div>
									<div>
										<p className="text-xs uppercase tracking-wide text-slate-400">Reçu le</p>
										<p className="text-sm font-semibold text-slate-800">{formatExactDateFr(selected.submittedAt) || "-"}</p>
										<span className="mt-0.5 inline-flex flex-wrap items-center gap-2">
											<EnvPill env={selected.sourceEnv} />
											{selected.section && <SectionPill section={selected.section} />}
											<span className="text-xs text-slate-400">{formatRelativeTimeFr(selected.submittedAt)}</span>
										</span>
									</div>
									{selected.readBy && (
										<div className="sm:col-span-2">
											<p className="text-xs uppercase tracking-wide text-slate-400">Lecture</p>
											<p className="text-sm text-slate-600">
												Lu par <span className="font-semibold">{selected.readBy}</span>
												{selected.readAt ? ` : ${formatRelativeTimeFr(selected.readAt)}` : ""}
											</p>
										</div>
									)}
								</div>

								{/* Le message complet, sauts de ligne préservés. */}
								<div className="rounded-xl border border-slate-200 bg-white p-4">
									<p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">{selected.message}</p>
								</div>
							</div>
						</Modal.Body>
						<Modal.Footer>
							{selected.status === "new" && (
								<GatedButton
									token={WRITE_TOKEN}
									onClick={() => applyStatus(selected, "read")}
									className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
								>
									<FiCheck className="h-4 w-4" /> Marquer lu
								</GatedButton>
							)}
							{selected.status === "archived" ? (
								<GatedButton
									token={WRITE_TOKEN}
									onClick={() => applyStatus(selected, "new")}
									className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
								>
									<FiRotateCcw className="h-4 w-4" /> Rouvrir
								</GatedButton>
							) : (
								<GatedButton
									token={WRITE_TOKEN}
									onClick={() => applyStatus(selected, "archived")}
									className="inline-flex items-center gap-1.5 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004a87]"
								>
									<FiArchive className="h-4 w-4" /> Archiver
								</GatedButton>
							)}
							<button
								type="button"
								onClick={closeModal}
								className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
							>
								Fermer
							</button>
						</Modal.Footer>
					</>
				)}
			</Modal>
		</div>
	);
};

export default ContactMessages;
